import { parsePrice, getSelctedPlanOfAPI } from '../components/APIBuyer/utils/apiPricingUtil';

/**
 * Centralized GTM Utility for Dealer Portal
 * Handles all dataLayer pushes for Fortellis GTM implementation.
 */

const UNDEFINED = null;

export const PAGE_SECTIONS = {
  DEALER_PORTAL: 'Dealer Portal',
  HOME: 'Home',
  API_DIRECTORY: 'API Directory',
  CHECKOUT: 'Checkout',
  APP_MANAGEMENT: 'App Management',
  MY_APIS: 'My APIs',
};

export const PAGE_SUBSECTIONS = {
  OVERVIEW: 'Overview',
  HOME: 'Home',
  MY_APIS: 'My APIs',
  APP_MANAGEMENT: 'App Management',
  APP_DOCUMENTS: 'App Documents',
  MANAGE_INTEGRATION: 'Manage Integration',
  MANAGE_FIELD_FILTERING: 'Manage Field Filtering',
  MANAGE_DMS_ACCOUNTS: 'Manage DMS Accounts',
  MANAGE_PRIVACY_PROTECTION: 'Manage Privacy Protection',
  MANAGE_API_SUBSCRIPTION_SETTINGS: 'Manage API subscription Settings',
  PRIVACY_PROTECTION: 'Privacy Protection',
  API_SUBSCRIPTION_SETTINGS: 'API Subscription Settings',
  ADD_LOCATIONS: 'Add Locations',
  REMOVE_LOCATIONS: 'Remove Locations',
  SELECT_LOCATIONS: 'Select Locations',
  AGREE_TO_TERMS: 'Agree to Terms',
  REVIEW_SUBSCRIPTION: 'Review Subscription',
  REVIEW_ACTIVATION: 'Review Activation',
  VERIFY_DMS_ACCOUNTS: 'Verify DMS Accounts',
  EDIT_API_FIELDS: 'Edit API Fields',
  COMPLETE_CONFIGURATION: 'Complete Configuration',
  MANAGE_BUNDLE_LOCATIONS: 'Manage Bundle locations',
  MANAGE_API_LOCATIONS: 'Manage API Locations',
  DATA_TRANSACTION_REPORT: 'Data Transaction Report',
  DEALER_INTEGRATION_REPORT: 'Dealer Integration Report',
  API_DIRECTORY: 'API Directory',
  API_DETAILS: 'API Details',
  BUNDLE_DETAILS: 'Bundle Details',
  REVIEW_CART: 'Review Cart',
  CONFIGURE_CMF: 'Configure CMF Number',
  TERMS_AND_CONDITIONS: 'Terms and Conditions',
  REVIEW_TERMS: 'Review Terms',
};

const toTitleCase = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  return trimmed
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const normalizeProduct = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'api' ? 'API' : 'APP';
};

export const getProductContext = (pageSubsection, path = '', pageSection = '') => {
  const cleanPath = typeof path === 'string' ? path.toLowerCase() : '';
  const cleanSection = typeof pageSection === 'string' ? pageSection.toLowerCase() : '';

  if (cleanSection === PAGE_SECTIONS.APP_MANAGEMENT.toLowerCase() || cleanPath.includes('/management')) {
    return 'APP';
  }

  if (
    cleanSection === PAGE_SECTIONS.API_DIRECTORY.toLowerCase() ||
    cleanSection === PAGE_SECTIONS.CHECKOUT.toLowerCase() ||
    cleanSection === PAGE_SECTIONS.MY_APIS.toLowerCase() ||
    cleanPath.includes('/api-directory') ||
    cleanPath.includes('/my-apis') ||
    cleanPath.includes('/checkout')
  ) {
    return 'API';
  }

  const apiSubsections = [
    PAGE_SUBSECTIONS.API_DIRECTORY,
    PAGE_SUBSECTIONS.API_DETAILS,
    PAGE_SUBSECTIONS.BUNDLE_DETAILS,
    PAGE_SUBSECTIONS.MY_APIS,
    PAGE_SUBSECTIONS.REVIEW_CART,
    PAGE_SUBSECTIONS.CONFIGURE_CMF,
    PAGE_SUBSECTIONS.TERMS_AND_CONDITIONS,
    PAGE_SUBSECTIONS.REVIEW_TERMS,
    PAGE_SUBSECTIONS.MANAGE_BUNDLE_LOCATIONS,
    PAGE_SUBSECTIONS.MANAGE_API_LOCATIONS,
  ];

  if (apiSubsections.includes(pageSubsection)) {
    return 'API';
  }

  return 'APP';
};

export const EVENTS = {
  FORTELLIS_PAGEVIEW: 'fortellis-pageview',
  FORTELLIS_EVENT: 'fortellis-event',
};

export const EVENT_ACTIONS = {
  API_ADDED_TO_CART: 'API Added to Cart',
  BUNDLE_ADDED_TO_CART: 'Bundle Added to Cart',
  API_INFORMATION_REQUESTED: 'API Information Requested',
  API_FILTER_CHANGED: 'API Filter Changed',
  API_FILTER_APPLIED: 'API Filter Applied',
  CHECKOUT_STARTED: 'Checkout Started',
  REVIEWED_CART: 'Reviewed Cart',
  SELECT_API_PLAN: 'Select API Plan',
  SELECT_BUNDLE_PLAN: 'Select Bundle Plan',
  REMOVE_BUNDLE_FROM_CART: 'Remove Bundle From Cart',
  REMOVE_API_FROM_CART: 'Remove API From Cart',
  CART_ITEMS_UPDATED: 'Cart Items Updated',
  TERMS_ACCEPTED: 'Terms Accepted',
  PURCHASE_COMPLETED: 'Purchase Completed',
  CHECKOUT_ERROR: 'Checkout Error',
  API_LOCATION_ENABLED: 'API Location Enabled',
  CONFIRM_ESTIMATED_MONTHLY_BILL: 'Confirm Estimated Monthly Bill',
  MANAGE_API_INTEGRATION: 'Manage API Integration',
  MANAGE_BUNDLE_INTEGRATION: 'Manage Bundle Integration',
  PC_COMPARE_PRICE: 'PC Compare Price',
  PC_SELECT_API_VERSION: 'PC Select API Version',
  PC_ADJUSTED_CALLS: 'PC Adjusted Calls per month',
};

export const API_TYPE_VALUES = {
  ASYNC_API: 'async-api',
  API: 'api',
  BUNDLE: 'bundle',
  BUNDLE_UPPER: 'BUNDLE',
};

export const API_TYPE_DISPLAY = {
  ASYNC: 'Async API',
  REST: 'REST API',
};

export const getFormattedApiType = (apiOrType) => {
  if (!apiOrType) return null;
  if (typeof apiOrType === 'string') {
    return apiOrType.toLowerCase().includes('async')
      ? API_TYPE_DISPLAY.ASYNC
      : API_TYPE_DISPLAY.REST;
  }
  const typeOrKind =
    apiOrType.type ||
    apiOrType.apiType ||
    apiOrType.kind ||
    apiOrType.resourceType;
  if (!typeOrKind && !apiOrType.name && !apiOrType.id) {
    return null;
  }
  return String(typeOrKind || '').toLowerCase().includes('async')
    ? API_TYPE_DISPLAY.ASYNC
    : API_TYPE_DISPLAY.REST;
};

export const ASYNC_API_ENDPOINT_NAME = 'Events per Month';

export const VIEW_TYPES = {
  GRID: 'grid',
};

export const PURCHASE_STATUS = {
  SUCCESS: 'success',
  FAILURE: 'failure',
};

const ROUTE_MAPPINGS = [
  {
    test: (path) => path.endsWith('/home') || path === '' || path === '/',
    data: { page_section: PAGE_SECTIONS.HOME, page_subsection: null }
  },
  {
    test: (path) => path.includes('/my-apis'),
    data: { page_section: PAGE_SECTIONS.MY_APIS, defaultSubsection: PAGE_SUBSECTIONS.OVERVIEW, disableGlobalTracking: true }
  },
  {
    test: (path) => path.includes('/management'),
    data: { page_section: PAGE_SECTIONS.APP_MANAGEMENT, defaultSubsection: PAGE_SUBSECTIONS.OVERVIEW, disableGlobalTracking: true }
  },
  {
    test: (path) => {
      const clean = path.replace(/^\/dp/, '');
      return /^\/[0-9a-fA-F-]{36}/.test(clean);
    },
    data: { page_section: PAGE_SECTIONS.APP_MANAGEMENT, defaultSubsection: PAGE_SUBSECTIONS.SELECT_LOCATIONS, disableGlobalTracking: true }
  },
  {
    test: (path) => path.includes('/data-transaction-report'),
    data: { page_section: PAGE_SECTIONS.APP_MANAGEMENT, defaultSubsection: PAGE_SUBSECTIONS.DATA_TRANSACTION_REPORT, disableGlobalTracking: true }
  },
  {
    test: (path) => path.includes('/dealer-integration-report'),
    data: { page_section: PAGE_SECTIONS.APP_MANAGEMENT, defaultSubsection: PAGE_SUBSECTIONS.DEALER_INTEGRATION_REPORT, disableGlobalTracking: true }
  },
  {
    test: (path) => path.includes('/api-directory'),
    data: { page_section: PAGE_SECTIONS.API_DIRECTORY, defaultSubsection: PAGE_SUBSECTIONS.OVERVIEW, disableGlobalTracking: true }
  },
  {
    test: (path) => path.includes('/checkout'),
    data: { page_section: PAGE_SECTIONS.CHECKOUT, defaultSubsection: PAGE_SUBSECTIONS.REVIEW_CART, disableGlobalTracking: true }
  }
];

export const pushToDataLayer = (data) => {
  window.dataLayer = window.dataLayer || [];
  if (!data || typeof data !== 'object') return;

  const sanitized = {};
  Object.keys(data).forEach((key) => {
    const val = data[key];
    if (val === undefined || val === '') {
      sanitized[key] = null;
    } else {
      sanitized[key] = val;
    }
  });

  window.dataLayer.push(sanitized);
};

export const getPageDataFromPath = (overrideSubsection, providedPath) => {
  const path = providedPath || (typeof window !== 'undefined' && window.location && window.location.pathname) || '';
  const cleanPath = path.replace(/\/$/, '').split('?')[0];

  const match = ROUTE_MAPPINGS.find(mapping => mapping.test(cleanPath));

  if (match) {
    const { page_section, page_subsection, defaultSubsection, disableGlobalTracking } = match.data;
    return {
      page_section: page_section || PAGE_SECTIONS.DEALER_PORTAL,
      page_subsection: overrideSubsection || page_subsection || defaultSubsection || null,
      disableGlobalTracking: !!disableGlobalTracking
    };
  }

  return {
    page_section: PAGE_SECTIONS.DEALER_PORTAL,
    page_subsection: overrideSubsection || null,
    disableGlobalTracking: false
  };
};

export const getGtmUserData = (providedLoginDetails) => {
  const storeModule = require('../redux/store/store');
  const store = storeModule.default || storeModule;
  const state = store.getState();
  const loginDetails = providedLoginDetails || state?.loginDetailsSlice;

  let userGuid = loginDetails?.userGuid;
  if (!userGuid && typeof window !== 'undefined') {
    try {
      const rawToken = localStorage.getItem('okta-token-storage');
      if (rawToken) {
        const parsed = JSON.parse(rawToken);
        userGuid = parsed?.idToken?.claims?.userGuid || parsed?.accessToken?.claims?.userGuid;
      }
    } catch (e) {
      // ignore
    }
  }

  const userEmail = loginDetails?.userEmail || '';
  const normalizedRole = toTitleCase(loginDetails?.role);
  const enterpriseId = loginDetails?.enterpriseId || null;
  const orgId = loginDetails?.entityDetails?.id || loginDetails?.fortellisEnterpriseEntityId || null;
  const fortellis_org_name = loginDetails?.entityDetails?.organizationName || null;

  const canPurchaseEnabled = loginDetails?.canPurchaseEnabled || [];
  const stores = Array.from(
    new Set(
      canPurchaseEnabled
        .map(item => item?.storeId)
        .filter(storeId => storeId !== undefined && storeId !== null && storeId !== '')
    )
  );

  return {
    user_id: userGuid || null,
    user_org: fortellis_org_name,
    user_role: normalizedRole ? [normalizedRole] : null,
    page_url: typeof window !== 'undefined' && window.location ? (window.location.origin + window.location.pathname) : null,
    page_path: typeof window !== 'undefined' && window.location ? window.location.pathname : null,
    is_impersonated_session: !!userEmail.toLowerCase().endsWith('@cdk.com'),
    fortellis_id: orgId,
    fortellis_org_name: fortellis_org_name,
    enterprise_id: enterpriseId,
    enterprise_name: fortellis_org_name,
    company_id: null,
    store_id: null,
    store_name: null,
    stores: stores,
    product_features: null,
    identified_id: null,
    application: PAGE_SECTIONS.DEALER_PORTAL,
  };
};

let lastTrackedPageData = null;
let pendingPageViews = [];
let isSubscribed = false;
let unsubscribe = null;

export const resetLastTrackedPageDataForTesting = () => {
  lastTrackedPageData = null;
  pendingPageViews = [];
  isSubscribed = false;
  unsubscribe = null;
};

const setupStoreSubscription = (store) => {
  if (isSubscribed) return;

  isSubscribed = true;
  unsubscribe = store.subscribe(() => {
    const state = store.getState();
    const loginDetails = state?.loginDetailsSlice;
    const { isAuthenticated, entityDetails, errorInGettingOrgName } = loginDetails || {};
    const orgName = entityDetails?.organizationName;

    // Flush once organization name is resolved (either successfully or via error)
    if (!isAuthenticated || orgName || errorInGettingOrgName) {
      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
      }
      isSubscribed = false;

      const viewsToFlush = [...pendingPageViews];
      pendingPageViews = [];
      viewsToFlush.forEach(({ subsectionOrPageData, providedPath, providedUserData }) => {
        trackPageView(subsectionOrPageData, providedPath, providedUserData);
      });
    }
  });
};

export const trackPageView = (subsectionOrPageData, providedPath, providedUserData) => {
  const pageData = (typeof subsectionOrPageData === 'string' || !subsectionOrPageData)
    ? getPageDataFromPath(subsectionOrPageData, providedPath)
    : subsectionOrPageData;

  pageData.page_section = pageData.page_section || PAGE_SECTIONS.DEALER_PORTAL;
  pageData.page_subsection = pageData.page_subsection || UNDEFINED;

  if (
    pageData.page_subsection === PAGE_SUBSECTIONS.API_DETAILS ||
    pageData.page_subsection === PAGE_SUBSECTIONS.BUNDLE_DETAILS ||
    pageData.page_subsection === PAGE_SUBSECTIONS.API_DIRECTORY ||
    (pageData.page_subsection === PAGE_SUBSECTIONS.OVERVIEW && pageData.page_section === PAGE_SECTIONS.API_DIRECTORY)
  ) {
    pageData.page_section = PAGE_SECTIONS.API_DIRECTORY;
  } else if (
    pageData.page_subsection === PAGE_SUBSECTIONS.REVIEW_CART ||
    pageData.page_subsection === PAGE_SUBSECTIONS.CONFIGURE_CMF ||
    pageData.page_subsection === PAGE_SUBSECTIONS.TERMS_AND_CONDITIONS ||
    pageData.page_subsection === PAGE_SUBSECTIONS.REVIEW_TERMS
  ) {
    pageData.page_section = PAGE_SECTIONS.CHECKOUT;
  } else if (
    pageData.page_subsection === PAGE_SUBSECTIONS.APP_MANAGEMENT ||
    pageData.page_subsection === PAGE_SUBSECTIONS.APP_DOCUMENTS ||
    pageData.page_subsection === 'App Documents' ||
    pageData.page_subsection === 'App documents' ||
    pageData.page_subsection === PAGE_SUBSECTIONS.MANAGE_DMS_ACCOUNTS ||
    pageData.page_subsection === 'Manage DMS Accounts' ||
    pageData.page_subsection === 'Manage DMS accounts' ||
    pageData.page_subsection === PAGE_SUBSECTIONS.MANAGE_FIELD_FILTERING ||
    pageData.page_subsection === 'Manage Field Filtering' ||
    pageData.page_subsection === 'Manage field filtering' ||
    pageData.page_subsection === PAGE_SUBSECTIONS.MANAGE_INTEGRATION ||
    pageData.page_subsection === 'Manage Integration' ||
    pageData.page_subsection === PAGE_SUBSECTIONS.MANAGE_PRIVACY_PROTECTION ||
    pageData.page_subsection === PAGE_SUBSECTIONS.PRIVACY_PROTECTION ||
    pageData.page_subsection === 'Manage Privacy Protection' ||
    pageData.page_subsection === 'Manage privacy protection' ||
    pageData.page_subsection === 'Privacy Protection' ||
    pageData.page_subsection === PAGE_SUBSECTIONS.MANAGE_API_SUBSCRIPTION_SETTINGS ||
    pageData.page_subsection === PAGE_SUBSECTIONS.API_SUBSCRIPTION_SETTINGS ||
    pageData.page_subsection === 'Manage API Subscription Settings' ||
    pageData.page_subsection === 'Manage API subscription settings' ||
    pageData.page_subsection === 'API Subscription Settings' ||
    pageData.page_subsection === PAGE_SUBSECTIONS.REMOVE_LOCATIONS ||
    pageData.page_subsection === 'Remove Locations' ||
    pageData.page_subsection === 'Remove locations' ||
    pageData.page_subsection === PAGE_SUBSECTIONS.ADD_LOCATIONS ||
    pageData.page_subsection === 'Add Locations' ||
    pageData.page_subsection === 'Add locations' ||
    pageData.page_subsection === PAGE_SUBSECTIONS.SELECT_LOCATIONS ||
    pageData.page_subsection === 'Select Locations' ||
    pageData.page_subsection === PAGE_SUBSECTIONS.AGREE_TO_TERMS ||
    pageData.page_subsection === 'Agree to Terms' ||
    pageData.page_subsection === PAGE_SUBSECTIONS.REVIEW_SUBSCRIPTION ||
    pageData.page_subsection === 'Review Subscription' ||
    pageData.page_subsection === PAGE_SUBSECTIONS.REVIEW_ACTIVATION ||
    pageData.page_subsection === 'Review Activation' ||
    pageData.page_subsection === PAGE_SUBSECTIONS.VERIFY_DMS_ACCOUNTS ||
    pageData.page_subsection === 'Verify DMS Accounts' ||
    pageData.page_subsection === PAGE_SUBSECTIONS.EDIT_API_FIELDS ||
    pageData.page_subsection === 'Edit API Fields' ||
    pageData.page_subsection === PAGE_SUBSECTIONS.COMPLETE_CONFIGURATION ||
    pageData.page_subsection === 'Complete Configuration' ||
    pageData.page_subsection === PAGE_SUBSECTIONS.DATA_TRANSACTION_REPORT ||
    pageData.page_subsection === PAGE_SUBSECTIONS.DEALER_INTEGRATION_REPORT ||
    (pageData.page_subsection === PAGE_SUBSECTIONS.OVERVIEW && pageData.page_section === PAGE_SECTIONS.APP_MANAGEMENT)
  ) {
    pageData.page_section = PAGE_SECTIONS.APP_MANAGEMENT;
  } else if (
    pageData.page_subsection === PAGE_SUBSECTIONS.MANAGE_BUNDLE_LOCATIONS ||
    pageData.page_subsection === 'Manage Bundle locations' ||
    pageData.page_subsection === PAGE_SUBSECTIONS.MANAGE_API_LOCATIONS ||
    pageData.page_subsection === 'Manage API Locations' ||
    pageData.page_subsection === PAGE_SUBSECTIONS.MY_APIS ||
    (pageData.page_subsection === PAGE_SUBSECTIONS.OVERVIEW && pageData.page_section === PAGE_SECTIONS.MY_APIS)
  ) {
    pageData.page_section = PAGE_SECTIONS.MY_APIS;
  }

  const storeModule = require('../redux/store/store');
  const store = storeModule.default || storeModule;
  const state = store.getState();
  const loginDetails = state?.loginDetailsSlice;
  const { isAuthenticated, entityDetails, errorInGettingOrgName } = loginDetails || {};
  const orgName = entityDetails?.organizationName;

  // Defer tracking if authenticated but organization details are still fetching
  if (isAuthenticated && !orgName && !errorInGettingOrgName) {
    pendingPageViews.push({ subsectionOrPageData, providedPath, providedUserData });
    setupStoreSubscription(store);
    return;
  }

  const userData = providedUserData || getGtmUserData();
  let extraData = {};

  if (
    pageData.page_subsection === PAGE_SUBSECTIONS.MY_APIS ||
    (pageData.page_section === PAGE_SECTIONS.MY_APIS && pageData.page_subsection === PAGE_SUBSECTIONS.OVERVIEW)
  ) {
    const orders = state?.orderAPISlice?.orders;
    const restApisLength = orders?.restApis?.length || 0;
    const asyncApisLength = orders?.asyncApis?.length || 0;

    extraData.api_count = String(restApisLength + asyncApisLength);
  } else if (
    pageData.page_subsection === PAGE_SUBSECTIONS.APP_MANAGEMENT ||
    (pageData.page_section === PAGE_SECTIONS.APP_MANAGEMENT && pageData.page_subsection === PAGE_SUBSECTIONS.OVERVIEW)
  ) {
    const counts = state?.subscriptionManagementSlice?.subscriptionCount || { active: 0, pending: 0, unsubscribed: 0 };
    extraData.app_count = String((counts.active || 0) + (counts.pending || 0) + (counts.unsubscribed || 0));
  } else if (pageData.page_subsection === PAGE_SUBSECTIONS.DATA_TRANSACTION_REPORT) {
    extraData.view_type = VIEW_TYPES.GRID;
  } else if (pageData.page_subsection === PAGE_SUBSECTIONS.REVIEW_CART) {
    const cart = state?.orderAPISlice?.cart || [];
    const bundleCount = cart.filter(item => item.resourceType === API_TYPE_VALUES.BUNDLE || item.resourceType === API_TYPE_VALUES.BUNDLE_UPPER).length;
    const apiCount = cart.length - bundleCount;
    extraData.api_cart_size = apiCount;
    extraData.bundle_cart_size = bundleCount;
  } else if (pageData.page_subsection === PAGE_SUBSECTIONS.API_DETAILS) {
    const apiInfo = state?.apiDirectorySlice?.apiInfo;
    const apiData = apiInfo?.data || {};
    const environments = apiData?.environments || [];

    const getVersionsForEnv = (envNames) => {
      const matchingEnvs = environments.filter((env) =>
        envNames.includes(env?.name?.toLowerCase()),
      );
      const versions = [];
      matchingEnvs.forEach((env) => {
        if (Array.isArray(env?.instances)) {
          env.instances.forEach((inst) => {
            const ver =
              inst?.version ||
              inst?.spec?.info?.version ||
              inst?.spec?.version;
            if (ver) versions.push(String(ver));
          });
        }
      });
      return versions.length > 0 ? [...new Set(versions)] : null;
    };

    const prodVersions = getVersionsForEnv(['prod', 'production']);
    const testVersions = getVersionsForEnv([
      'test',
      'testing',
      'stage',
      'staging',
      'dev',
      'development',
    ]);

    extraData.api_name =
      pageData.api_name !== undefined
        ? pageData.api_name
        : (apiData?.name || null);
    extraData.api_id =
      pageData.api_id !== undefined ? pageData.api_id : (apiData?.id || null);
    extraData.api_type =
      pageData.api_type !== undefined
        ? pageData.api_type
        : (apiData ? getFormattedApiType(apiData) : null);
    extraData.api_prod_version =
      pageData.api_prod_version !== undefined
        ? pageData.api_prod_version
        : prodVersions;
    extraData.api_test_version =
      pageData.api_test_version !== undefined
        ? pageData.api_test_version
        : testVersions;
  } else if (pageData.page_subsection === PAGE_SUBSECTIONS.BUNDLE_DETAILS) {
    const bundleData = state?.apiDirectorySlice?.bundleData;
    const apis = bundleData?.apis || [];
    const apiIds = apis
      .map((api) => (typeof api === 'string' ? api : api?.id))
      .filter(Boolean);
    const apiIdsArr =
      apiIds.length > 0 ? [...new Set(apiIds.map(String))] : null;

    extraData.bundle_name =
      pageData.bundle_name !== undefined
        ? pageData.bundle_name
        : (bundleData?.name || null);
    extraData.bundle_id =
      pageData.bundle_id !== undefined
        ? pageData.bundle_id
        : (bundleData?.id || null);
    extraData.api_ids =
      pageData.api_ids !== undefined ? pageData.api_ids : apiIdsArr;
  }

  // Deduplication logic: Skip if page_section and page_subsection are identical to the last tracked one
  const currentTrackingData = JSON.stringify({
    section: pageData.page_section,
    subsection: pageData.page_subsection
  });

  if (lastTrackedPageData === currentTrackingData) {
    return;
  }

  lastTrackedPageData = currentTrackingData;

  const product = normalizeProduct(
    pageData.product || getProductContext(
      pageData.page_subsection,
      providedPath || (typeof window !== 'undefined' && window.location && window.location.pathname),
      pageData.page_section,
    ),
  );
  const { disableGlobalTracking, ...cleanPageData } = pageData;

  pushToDataLayer({
    event: EVENTS.FORTELLIS_PAGEVIEW,
    application: PAGE_SECTIONS.DEALER_PORTAL,
    ...cleanPageData,
    ...userData,
    product,
    ...extraData
  });
};

export const getCartPricingDetails = (cart = []) => {
  let totalBill = 0;
  let totalInstallFee = 0;

  cart.forEach((resource) => {
    const plan = getSelctedPlanOfAPI(resource);
    if (plan) {
      totalBill += parsePrice(plan);
      const shouldShowInstallFee = (resource.resourceType?.toLowerCase() === API_TYPE_VALUES.BUNDLE || plan.isDealerPlan) && plan.installFee != null;
      if (shouldShowInstallFee) {
        totalInstallFee += Number(plan.installFee) || 0;
      }
    }
  });

  return {
    totalBill,
    totalInstallFee,
    totalPrice: totalBill + totalInstallFee,
  };
};

const formatPrice = (price) => {
  const parsed = Number(price);
  return isNaN(parsed) ? '$0.00' : `$${parsed.toFixed(2)}`;
};

export const trackApiAddedToCart = (apiData, plan, cartSize) => {
  const price = plan ? formatPrice(parsePrice(plan)) : null;
  pushToDataLayer({
    event: EVENTS.FORTELLIS_EVENT,
    event_action: EVENT_ACTIONS.API_ADDED_TO_CART,
    api_name: apiData?.name || null,
    api_id: apiData?.id || null,
    api_type: getFormattedApiType(apiData),
    plan_selected: plan?.name || null,
    price,
    cart_size: cartSize != null ? cartSize : null,
  });
};

export const trackBundleAddedToCart = (bundleData, plan, cartSize) => {
  const price = plan ? formatPrice(parsePrice(plan)) : null;
  const apis = bundleData?.apis || [];
  const apiIds = apis.map((api) => (typeof api === 'string' ? api : api?.id)).filter(Boolean);

  pushToDataLayer({
    event: EVENTS.FORTELLIS_EVENT,
    event_action: EVENT_ACTIONS.BUNDLE_ADDED_TO_CART,
    bundle_name: bundleData?.name || null,
    bundle_id: bundleData?.id || null,
    api_ids: apiIds.length > 0 ? [...new Set(apiIds.map(String))] : null,
    plan_selected: plan?.name || plan?.planName || null,
    price,
    cart_size: cartSize != null ? cartSize : null,
  });
};

export const trackApiInformationRequested = (apiData, cartSize) => {
  pushToDataLayer({
    event: EVENTS.FORTELLIS_EVENT,
    event_action: EVENT_ACTIONS.API_INFORMATION_REQUESTED,
    api_name: apiData?.name || null,
    api_id: apiData?.id || null,
    api_type: getFormattedApiType(apiData),
    plan_selected: null,
    price: null,
    cart_size: cartSize != null ? cartSize : null,
  });
};

export const trackApiFilterChanged = ({
  filterType,
  filterValue,
  action,
  activeFilters,
  resultsCount,
}) => {
  const totalActiveFilters =
    (activeFilters?.api_group?.length || 0) +
    (activeFilters?.api_type?.length || 0) +
    (activeFilters?.categories?.length || 0);

  pushToDataLayer({
    event: EVENTS.FORTELLIS_EVENT,
    event_action: EVENT_ACTIONS.API_FILTER_CHANGED,
    filter_type: filterType || null,
    filter_value: filterValue || null,
    action: action || null,
    active_filters: activeFilters || null,
    total_active_filters: totalActiveFilters,
    results_count: resultsCount != null ? resultsCount : null,
  });
};

/**
 * @deprecated Use trackApiFilterChanged instead
 */
export const trackApiFilterApplied = (filterType, filterValue, resultsCount) => {
  pushToDataLayer({
    event: EVENTS.FORTELLIS_EVENT,
    event_action: EVENT_ACTIONS.API_FILTER_APPLIED,
    filter_type: filterType || null,
    filter_value: filterValue || null,
    results_count: resultsCount != null ? resultsCount : null,
  });
};

export const trackCheckoutStarted = (apiItemsSize, bundleItemsSize) => {
  pushToDataLayer({
    event: EVENTS.FORTELLIS_EVENT,
    event_action: EVENT_ACTIONS.CHECKOUT_STARTED,
    api_items_size: apiItemsSize != null ? apiItemsSize : null,
    bundle_items_size: bundleItemsSize != null ? bundleItemsSize : null,
  });
};

export const trackReviewedCart = (totalPrice, totalInstallFee, apiItemsSize, bundleItemsSize) => {
  pushToDataLayer({
    event: EVENTS.FORTELLIS_EVENT,
    event_action: EVENT_ACTIONS.REVIEWED_CART,
    total_price: totalPrice != null ? formatPrice(totalPrice) : null,
    total_install_fee: totalInstallFee != null ? formatPrice(totalInstallFee) : null,
    api_items_size: apiItemsSize != null ? apiItemsSize : null,
    bundle_items_size: bundleItemsSize != null ? bundleItemsSize : null,
  });
};

export const trackSelectApiPlan = (planSelectedName, apiId, apiDisplayName, apiType) => {
  pushToDataLayer({
    event: EVENTS.FORTELLIS_EVENT,
    event_action: EVENT_ACTIONS.SELECT_API_PLAN,
    plan_selected: planSelectedName || null,
    api_id: apiId || null,
    api_display_name: apiDisplayName || null,
    api_type: getFormattedApiType(apiType),
  });
};

export const trackSelectBundlePlan = (planName, bundleId, bundleDisplayName) => {
  pushToDataLayer({
    event: EVENTS.FORTELLIS_EVENT,
    event_action: EVENT_ACTIONS.SELECT_BUNDLE_PLAN,
    plan_selected: planName || null,
    bundle_id: bundleId || null,
    bundle_display_name: bundleDisplayName || null,
  });
};

export const trackRemoveFromCart = (item, currentCart = []) => {
  const bundleCount = currentCart.filter(
    (x) => x.resourceType?.toLowerCase() === API_TYPE_VALUES.BUNDLE
  ).length;
  const apiCount = currentCart.length - bundleCount;

  const isBundle = item?.resourceType?.toLowerCase() === API_TYPE_VALUES.BUNDLE;
  const newApiCount = isBundle ? apiCount : Math.max(0, apiCount - 1);
  const newBundleCount = isBundle ? Math.max(0, bundleCount - 1) : bundleCount;

  if (isBundle) {
    const apis = item?.details?.apis || [];
    const apiIds = apis.map((api) => (typeof api === 'string' ? api : api?.id)).filter(Boolean);

    pushToDataLayer({
      event: EVENTS.FORTELLIS_EVENT,
      event_action: EVENT_ACTIONS.REMOVE_BUNDLE_FROM_CART,
      bundle_id: item?.resourceId ? [item.resourceId] : null,
      api_ids: apiIds.length > 0 ? [...new Set(apiIds.map(String))] : null,
    });
  } else {
    pushToDataLayer({
      event: EVENTS.FORTELLIS_EVENT,
      event_action: EVENT_ACTIONS.REMOVE_API_FROM_CART,
      api_id: item?.resourceId || null,
      api_display_name: item?.details?.name || null,
      api_type: getFormattedApiType(item),
    });
  }

  pushToDataLayer({
    event: EVENTS.FORTELLIS_EVENT,
    event_action: EVENT_ACTIONS.CART_ITEMS_UPDATED,
    api_cart_size: newApiCount,
    bundle_cart_size: newBundleCount,
  });
};

export const trackTermsAccepted = () => {
  pushToDataLayer({
    event: EVENTS.FORTELLIS_EVENT,
    event_action: EVENT_ACTIONS.TERMS_ACCEPTED,
    accepted_at: new Date().toISOString(),
  });
};

export const trackPurchaseCompleted = ({
  totalPrice,
  totalInstallFee,
  apiItemsSize,
  bundleCartSize,
  purchaseStatus,
  orderId,
}) => {
  pushToDataLayer({
    event: EVENTS.FORTELLIS_EVENT,
    event_action: EVENT_ACTIONS.PURCHASE_COMPLETED,
    order_id: orderId || null,
    total_price: totalPrice != null ? formatPrice(totalPrice) : null,
    total_install_fee: totalInstallFee != null ? formatPrice(totalInstallFee) : null,
    bundle_cart_size: bundleCartSize != null ? bundleCartSize : null,
    api_items_size: apiItemsSize != null ? apiItemsSize : null,
    purchase_status: purchaseStatus || null,
  });
};

export const CHECKOUT_ERROR_CODES = {
  FETCH_CART_FAILED: 'FETCH_CART_FAILED',
  DELETE_API_FAILED: 'DELETE_API_FAILED',
  UPDATE_PRICING_PLAN_FAILED: 'UPDATE_PRICING_PLAN_FAILED',
  CREATE_ORDER_FAILED: 'CREATE_ORDER_FAILED',
  UPDATE_ORDER_FAILED: 'UPDATE_ORDER_FAILED',
};

export const CHECKOUT_ERROR_KEYS = {
  ERROR_FETCHING_CART_DETAILS: 'errorFetchingCartDetails',
  ERROR_DELETING_API: 'errorDeletingApi',
  ERROR_ADDING_PRICING_PLAN: 'errorAddingPricingPlanToApiInCart',
  ERROR_CREATING_ORDER: 'errorCreatingOrder',
  ERROR_UPDATING_ORDER: 'errorUpdatingOrder',
};

export const CHECKOUT_ERROR_MESSAGES = {
  FETCH_CART_FAILED: 'Failed to fetch cart details',
  DELETE_API_FAILED: 'Failed to remove item from cart',
  UPDATE_PRICING_PLAN_FAILED: 'Failed to update pricing plan',
  CREATE_ORDER_FAILED: 'Failed to create order',
  UPDATE_ORDER_FAILED: 'Failed to update order',
};

export const trackCheckoutError = ({ errorCode, errorMessage, step }) => {
  pushToDataLayer({
    event: EVENTS.FORTELLIS_EVENT,
    event_action: EVENT_ACTIONS.CHECKOUT_ERROR,
    error_code: errorCode || null,
    error_message: errorMessage || null,
    step: step || null,
  });
};

export const trackApiLocationEnabled = ({ apiId, apiDisplayName, apiType, locationCount }) => {
  pushToDataLayer({
    event: EVENTS.FORTELLIS_EVENT,
    event_action: EVENT_ACTIONS.API_LOCATION_ENABLED,
    api_id: apiId || null,
    api_display_name: apiDisplayName || null,
    api_type: apiType || null,
    location_count: locationCount != null ? String(locationCount) : null,
  });
};

export const trackConfirmEstimatedMonthlyBill = (bundleIdOrProps, bundleDisplayName, locationCount) => {
  let bundleId = null;
  let name = null;
  let count = '0';

  if (typeof bundleIdOrProps === 'object' && bundleIdOrProps !== null) {
    bundleId = bundleIdOrProps.bundleId || bundleIdOrProps.bundle_id || null;
    name = bundleIdOrProps.bundleDisplayName || bundleIdOrProps.bundle_display_name || null;
    count = bundleIdOrProps.locationCount ?? bundleIdOrProps.location_count ?? '0';
  } else {
    bundleId = bundleIdOrProps || null;
    name = bundleDisplayName || null;
    count = locationCount ?? '0';
  }

  pushToDataLayer({
    event: EVENTS.FORTELLIS_EVENT,
    event_action: EVENT_ACTIONS.CONFIRM_ESTIMATED_MONTHLY_BILL,
    bundle_id: bundleId,
    bundle_display_name: name,
    location_count: count != null ? String(count) : null,
  });
};

export const trackComparePrice = () => {
  pushToDataLayer({
    event: EVENTS.FORTELLIS_EVENT,
    event_action: EVENT_ACTIONS.PC_COMPARE_PRICE,
  });
};

export const trackSelectApiVersion = (apiVersion) => {
  pushToDataLayer({
    event: EVENTS.FORTELLIS_EVENT,
    event_action: EVENT_ACTIONS.PC_SELECT_API_VERSION,
    api_version: apiVersion || null,
  });
};

const debounceTimers = {};

export const trackAdjustedCalls = (endpointName, callsPerMonth, apiVersion) => {
  const key = endpointName || 'default';
  if (debounceTimers[key]) {
    clearTimeout(debounceTimers[key]);
  }
  debounceTimers[key] = setTimeout(() => {
    const isAsync = endpointName === ASYNC_API_ENDPOINT_NAME;
    const payload = {
      event: EVENTS.FORTELLIS_EVENT,
      event_action: EVENT_ACTIONS.PC_ADJUSTED_CALLS,
      calls_per_month: callsPerMonth != null && callsPerMonth !== '' ? String(callsPerMonth) : null,
      end_point_name: isAsync ? null : (endpointName || null),
      api_version: isAsync ? null : (apiVersion || null),
    };

    pushToDataLayer(payload);
    delete debounceTimers[key];
  }, 1000);
};

export const trackManageApiIntegration = ({
  actionType,
  planSelected,
  apiId,
  apiDisplayName,
  apiType,
  bundleId,
  bundleDisplayName,
  isBundle,
}) => {
  const isBundleItem = Boolean(isBundle || bundleId);
  const payload = {
    event: EVENTS.FORTELLIS_EVENT,
    event_action: isBundleItem
      ? EVENT_ACTIONS.MANAGE_BUNDLE_INTEGRATION
      : EVENT_ACTIONS.MANAGE_API_INTEGRATION,
    action_type: actionType || null,
  };

  if (planSelected) {
    payload.plan_selected = planSelected;
  }

  if (isBundleItem) {
    payload.bundle_id = bundleId || apiId || null;
    payload.bundle_display_name = bundleDisplayName || apiDisplayName || null;
  } else {
    payload.api_id = apiId || null;
    payload.api_display_name = apiDisplayName || null;
    payload.api_type = getFormattedApiType(apiType) || apiType || null;
  }

  pushToDataLayer(payload);
};

